// Back office: Guest chat (docs/GUEST_CHAT_2026-10-28.md). The same chats the
// dining screens answer (/api/dining/chat/*, chat.reply): guests write from
// the restaurant's public pages; the owner / manager reads, replies, books the
// table asked for and closes the chat here. The page re-reads every few
// seconds while it is open and checks in so guests see someone online.
// Every message is rendered as text.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { CalendarDays, CheckCircle2, Loader2, MessageCircle, Phone, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { API } from '@/config/api';

const LIST_MS = 5000;
const THREAD_MS = 3000;
const CHECK_IN_MS = 45000;

function useChatApi(token, restaurantId) {
  return useCallback(async (path, { method = 'GET', body } = {}) => {
    const headers = { Authorization: `Bearer ${token}`, Accept: 'application/json' };
    if (restaurantId) headers['X-Merchant-ID'] = String(restaurantId);
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    let res;
    try {
      res = await fetch(`${API}/dining/chat${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    } catch (e) {
      throw new Error('Could not reach chain29 - check the connection.');
    }
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const d = data?.detail;
      const err = new Error(typeof d === 'string' ? d : d?.message || `Request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  }, [token, restaurantId]);
}

function usePoll(fn, ms, deps) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const ref = useRef(fn);
  ref.current = fn;
  const reload = useCallback(async () => {
    try {
      const next = await ref.current();
      setData(next);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, []);
  useEffect(() => {
    setData(null);
    reload();
    const t = setInterval(() => { if (document.visibilityState !== 'hidden') reload(); }, ms);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, error, reload };
}

function time(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const requestLine = (r) => (r ? `${r.party_size} ${r.party_size === 1 ? 'guest' : 'guests'} · ${r.date} · ${r.time}` : null);
const smsLabel = (s) => ({ sent: 'texted to the guest', failed: 'text could not be sent', sending: 'texting…' }[s] || null);
const field = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white';

function BookDialog({ conversation, call, onClose, onBooked }) {
  const asked = conversation.reservation_request || {};
  const [v, setV] = useState({
    party_size: asked.party_size || 2, date: asked.date || new Date().toISOString().slice(0, 10), time: asked.time || '19:00',
    customer_name: conversation.guest_name || '', customer_phone: conversation.guest_phone || '', special_requests: asked.note || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: k === 'party_size' ? Number(e.target.value) : e.target.value }));
  const book = async () => {
    setBusy(true);
    setError(null);
    try {
      const a = await call(`/conversations/${conversation.id}/reservation`, { method: 'POST', body: { ...v, special_requests: v.special_requests || null } });
      toast.success(`Booked · ${a.reservation.confirmation_code}`);
      onBooked();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Book a table">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
        <div className="mb-4 flex items-start gap-3">
          <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Book a table</h2>
            <p className="text-sm text-slate-500">Your booking rules and a free table that fits, as for an online booking. The guest sees the confirmation in the chat.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Guests<input type="number" min={1} max={50} value={v.party_size} onChange={set('party_size')} className={`${field} mt-1`} /></label>
          <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Date<input type="date" value={v.date} onChange={set('date')} className={`${field} mt-1`} /></label>
          <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Time<input type="time" step={900} value={v.time} onChange={set('time')} className={`${field} mt-1`} /></label>
          <label className="col-span-3 text-xs font-semibold text-slate-600 sm:col-span-1 dark:text-slate-300">Name<input value={v.customer_name} maxLength={100} onChange={set('customer_name')} className={`${field} mt-1`} /></label>
          <label className="col-span-3 text-xs font-semibold text-slate-600 sm:col-span-2 dark:text-slate-300">Phone<input type="tel" value={v.customer_phone} onChange={set('customer_phone')} className={`${field} mt-1`} placeholder="Ask the guest for one" /></label>
          <label className="col-span-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Special requests<input value={v.special_requests} maxLength={500} onChange={set('special_requests')} className={`${field} mt-1`} /></label>
        </div>
        {error && <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={book} disabled={busy} className="bg-orange-500 text-white hover:bg-orange-600">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarDays className="mr-2 h-4 w-4" />}Book table
          </Button>
        </div>
      </div>
    </div>
  );
}

function Thread({ id, call, canBook, onChanged }) {
  const thread = usePoll(() => call(`/conversations/${id}`), THREAD_MS, [id]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(false);
  const listRef = useRef(null);
  const conv = thread.data?.conversation;
  const messages = useMemo(() => thread.data?.messages || [], [thread.data]);

  useEffect(() => {
    if (conv?.unread > 0 && document.visibilityState !== 'hidden') call(`/conversations/${id}/read`, { method: 'POST' }).then(onChanged).catch(() => {});
  }, [conv, id, call, onChanged]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await call(`/conversations/${id}/messages`, { method: 'POST', body: { body, client_id: (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}` } });
      setDraft('');
      thread.reload();
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const close = async () => {
    try {
      await call(`/conversations/${id}/close`, { method: 'POST' });
      thread.reload();
      onChanged();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (thread.error && !thread.data) return <p role="alert" className="p-6 text-sm text-red-600">{thread.error.message}</p>;
  if (!conv) return <p className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Opening chat…</p>;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-5 py-3 dark:border-slate-700">
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-bold text-slate-900 dark:text-white">{conv.guest_name}</p>
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            <span className={`h-2 w-2 rounded-full ${conv.guest_here ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            {conv.guest_here ? 'On the page now' : 'Away'}
            {(conv.guest_phone || conv.guest_email) && <><span>·</span><Phone className="h-3 w-3" />{[conv.guest_phone, conv.guest_email].filter(Boolean).join(' · ')}</>}
            {conv.sms_opt_in && <span>· gets replies by text when away</span>}
          </p>
        </div>
        {conv.reservation
          ? <span className="flex items-center gap-1 text-sm font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" />{conv.reservation.confirmation_code} · {conv.reservation.date} {conv.reservation.time}</span>
          : canBook && <Button size="sm" variant="outline" onClick={() => setBooking(true)}><CalendarDays className="mr-1.5 h-4 w-4" />Book table</Button>}
        {conv.status === 'open' && <Button size="sm" variant="outline" onClick={close}>Close chat</Button>}
      </div>
      {conv.reservation_request && !conv.reservation && (
        <div className="mx-5 mt-3 flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-800 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-200">
          <CalendarDays className="h-4 w-4" />Asked for a table: {requestLine(conv.reservation_request)}{conv.reservation_request.note ? ` · ${conv.reservation_request.note}` : ''}
        </div>
      )}
      <div ref={listRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-5" aria-live="polite">
        {messages.map((m) => {
          if (m.sender === 'system') {
            return <p key={m.id} className={`mx-auto max-w-[85%] whitespace-pre-wrap break-words rounded-xl px-3 py-1.5 text-center text-xs font-medium ${m.kind === 'reservation_created' ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'}`}>{m.body}</p>;
          }
          const staff = m.sender === 'staff';
          return (
            <div key={m.id} className={`flex flex-col ${staff ? 'items-end' : 'items-start'}`}>
              <div className={`max-w-[75%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm ${staff ? 'rounded-br-md bg-orange-500 text-white' : 'rounded-bl-md bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white'}`}>
                {m.kind === 'reservation_request' && <span className="mb-1 flex items-center gap-1 text-xs font-bold opacity-80"><CalendarDays className="h-3 w-3" />Table request</span>}
                {m.body}
              </div>
              <span className="mt-1 text-[11px] text-slate-400">{[staff ? m.author_name : null, time(m.created_at), staff ? smsLabel(m.sms_status) : null].filter(Boolean).join(' · ')}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-end gap-2 border-t border-slate-200 p-3 dark:border-slate-700">
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} maxLength={2000} aria-label="Reply"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={conv.status === 'closed' ? 'Write to open this chat again' : `Reply to ${conv.guest_name}`} className={`${field} resize-none`} />
        <Button onClick={send} disabled={busy || !draft.trim()} className="bg-orange-500 text-white hover:bg-orange-600">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      {booking && <BookDialog conversation={conv} call={call} onClose={() => setBooking(false)} onBooked={() => { thread.reload(); onChanged(); }} />}
    </div>
  );
}

export default function GuestChatPage() {
  const { token, activeRestaurantId } = useOutletContext();
  const call = useChatApi(token, activeRestaurantId);
  const [status, setStatus] = useState('open');
  const [params, setParams] = useSearchParams();
  const selected = Number(params.get('c')) || null;
  const list = usePoll(() => call(`/conversations?status=${status}`), LIST_MS, [status, call]);

  useEffect(() => {
    const ping = () => { if (document.visibilityState !== 'hidden') call('/presence', { method: 'POST' }).catch(() => {}); };
    ping();
    const t = setInterval(ping, CHECK_IN_MS);
    return () => clearInterval(t);
  }, [call]);

  const pick = (id) => {
    const next = new URLSearchParams(params);
    next.set('c', String(id));
    setParams(next, { replace: true });
  };
  const rows = list.data?.conversations || [];
  const canBook = true; // everyone who answers chat (chat.reply) may book from it; the server answers 403 (shown) otherwise

  return (
    <div className="mx-auto max-w-6xl" data-testid="guest-chat-page">
      <h1 className="mb-1 text-2xl font-bold text-slate-900 dark:text-white">Guest chat</h1>
      <p className="mb-4 text-sm text-slate-500">Guests write from your restaurant&apos;s page, online ordering and reservations. Your staff answer the same chats on the POS.</p>
      {list.error?.status === 403 && <p role="alert" className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">Your role cannot answer guest chat (chat.reply).</p>}
      <div className="flex h-[calc(100vh-14rem)] min-h-[480px] overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        <aside className="flex w-80 flex-none flex-col border-r border-slate-200 dark:border-slate-700">
          <div className="flex gap-1 border-b border-slate-200 p-2 dark:border-slate-700">
            {['open', 'closed', 'all'].map((s) => (
              <button key={s} type="button" onClick={() => setStatus(s)} aria-pressed={status === s}
                className={`flex-1 rounded-lg py-1.5 text-sm font-medium capitalize ${status === s ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>{s}</button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {!list.data && !list.error && <p className="flex items-center gap-2 p-4 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</p>}
            {list.error && list.error.status !== 403 && <p role="alert" className="p-4 text-sm text-red-600">{list.error.message}</p>}
            {list.data && !rows.length && (
              <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-slate-500"><MessageCircle className="h-8 w-8 text-slate-300" />No {status === 'all' ? '' : status} chats.</div>
            )}
            {rows.map((c) => (
              <button key={c.id} type="button" onClick={() => pick(c.id)}
                className={`mb-1 w-full rounded-xl p-3 text-left ${c.id === selected ? 'bg-orange-50 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'}`}>
                <div className="flex items-center gap-2">
                  <span className={`flex-1 truncate text-sm ${c.unread ? 'font-bold' : 'font-medium'} text-slate-900 dark:text-white`}>{c.guest_name}</span>
                  {c.unread > 0 && <span className="rounded-full bg-orange-500 px-2 text-xs font-bold text-white">{c.unread}</span>}
                </div>
                {c.topic === 'reservation' && <span className={`text-xs font-semibold ${c.reservation_id ? 'text-emerald-600' : 'text-orange-600'}`}>{c.reservation_id ? 'Table booked' : requestLine(c.reservation_request)}</span>}
                <p className="truncate text-xs text-slate-500">{c.last_message ? `${c.last_message.sender === 'staff' ? 'You: ' : ''}${c.last_message.preview}` : ''}</p>
                <p className="text-[11px] text-slate-400">{time(c.last_message_at)}</p>
              </button>
            ))}
          </div>
        </aside>
        <section className="flex min-w-0 flex-1">
          {selected
            ? <Thread key={selected} id={selected} call={call} canBook={canBook} onChanged={list.reload} />
            : <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-sm text-slate-500"><MessageCircle className="h-10 w-10 text-slate-300" />Pick a chat to answer it.</div>}
        </section>
      </div>
    </div>
  );
}
